import React, { useState, useEffect, useCallback } from 'react';
import { adminService } from '../services/api';
import './OrdersManagement.css';

const BienestarBookingsManagement = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSchedule, setFilterSchedule] = useState('');
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [actionId, setActionId] = useState(null);
  const [scheduleDraft, setScheduleDraft] = useState({});

  const fetchBookings = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page: pagination.page, limit: pagination.limit };
      if (filterStatus) params.status = filterStatus;
      if (filterSchedule) params.schedule_status = filterSchedule;
      const res = await adminService.getBienestarBookings(params);
      const list = res?.bookings ?? res?.data?.bookings ?? [];
      setBookings(list);
      setPagination((prev) => ({
        ...prev,
        ...(res?.pagination ?? res?.data?.pagination ?? {}),
        total: res?.pagination?.total ?? res?.data?.pagination?.total ?? list.length,
        pages: res?.pagination?.pages ?? res?.data?.pagination?.pages ?? 1,
      }));
    } catch (error) {
      console.error('Error fetching bienestar bookings:', error);
      setMessage({ type: 'error', text: 'Error al cargar reservas de bienestar' });
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filterStatus, filterSchedule]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr.replace(' ', 'T')).toLocaleString('es-CO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'America/Bogota',
    });
  };

  const getStatusBadge = (status) => {
    const map = {
      confirmed: { label: 'Confirmada', class: 'status-paid' },
      cancelled: { label: 'Cancelada', class: 'status-cancelled' },
    };
    const b = map[status] || { label: status || '—', class: 'status-default' };
    return <span className={`status-badge ${b.class}`}>{b.label}</span>;
  };

  const getScheduleBadge = (status) => {
    const map = {
      scheduled: { label: 'Agendada', class: 'status-shipped' },
      pending: { label: 'Sin horario', class: 'status-pending' },
    };
    const b = map[status] || { label: status || '—', class: 'status-default' };
    return <span className={`status-badge ${b.class}`}>{b.label}</span>;
  };

  const callUpdate = async (id, body) => {
    try {
      setActionId(id);
      setMessage({ type: '', text: '' });
      await adminService.updateBienestarBooking(id, body);
      setMessage({ type: 'success', text: 'Reserva actualizada' });
      await fetchBookings();
    } catch (e) {
      setMessage({
        type: 'error',
        text: e.response?.data?.message || e.message || 'Error al actualizar',
      });
    } finally {
      setActionId(null);
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('¿Cancelar esta reserva?')) return;
    await callUpdate(id, { status: 'cancelled' });
  };

  const handleSaveSchedule = async (id) => {
    const raw = scheduleDraft[id];
    if (!raw) {
      setMessage({ type: 'error', text: 'Indica fecha y hora' });
      return;
    }
    const slotStart = raw.length === 16 ? `${raw}:00` : raw;
    await callUpdate(id, { scheduledAt: slotStart, scheduleStatus: 'scheduled' });
  };

  return (
    <div className="orders-management">
      <div className="orders-header">
        <h2>Reservas Bienestar</h2>
        <p className="orders-subtitle">Primera sesión gratis, cupones y calendario 1 a 1</p>
      </div>

      {message.text && (
        <div className={`orders-message orders-message--${message.type}`}>{message.text}</div>
      )}

      <div className="orders-filters">
        <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setPagination((p) => ({ ...p, page: 1 })); }}>
          <option value="">Todos los estados</option>
          <option value="confirmed">Confirmadas</option>
          <option value="cancelled">Canceladas</option>
        </select>
        <select value={filterSchedule} onChange={(e) => { setFilterSchedule(e.target.value); setPagination((p) => ({ ...p, page: 1 })); }}>
          <option value="">Agenda: todas</option>
          <option value="pending">Sin horario</option>
          <option value="scheduled">Con horario</option>
        </select>
        <button type="button" className="orders-refresh-btn" onClick={fetchBookings} disabled={loading}>
          <i className="fas fa-sync-alt" /> Actualizar
        </button>
      </div>

      {loading ? (
        <p className="orders-loading">Cargando…</p>
      ) : bookings.length === 0 ? (
        <p className="orders-empty">No hay reservas con estos filtros.</p>
      ) : (
        <div className="orders-table-wrap">
          <table className="orders-table">
            <thead>
              <tr>
                <th>Ref</th>
                <th>Servicio</th>
                <th>Cliente</th>
                <th>Horario</th>
                <th>Estado</th>
                <th>Agenda</th>
                <th>Creada</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((b) => (
                <tr key={b.id}>
                  <td><code>{b.bookingRef}</code></td>
                  <td>{b.serviceName}</td>
                  <td>
                    <div>{b.customerName}</div>
                    <small>{b.email}</small>
                  </td>
                  <td>{formatDate(b.scheduledAt)}</td>
                  <td>{getStatusBadge(b.status)}</td>
                  <td>{getScheduleBadge(b.scheduleStatus)}</td>
                  <td>{formatDate(b.createdAt)}</td>
                  <td className="orders-actions">
                    {b.status === 'confirmed' && (
                      <>
                        <input
                          type="datetime-local"
                          className="orders-tracking-input"
                          value={scheduleDraft[b.id] ?? ''}
                          onChange={(e) => setScheduleDraft((prev) => ({ ...prev, [b.id]: e.target.value }))}
                          title="Nuevo horario (hora Colombia)"
                        />
                        <button
                          type="button"
                          className="orders-action-btn"
                          disabled={actionId === b.id}
                          onClick={() => handleSaveSchedule(b.id)}
                        >
                          Guardar hora
                        </button>
                        <button
                          type="button"
                          className="orders-action-btn orders-action-btn--danger"
                          disabled={actionId === b.id}
                          onClick={() => handleCancel(b.id)}
                        >
                          Cancelar
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pagination.pages > 1 && (
        <div className="orders-pagination">
          <button
            type="button"
            disabled={pagination.page <= 1}
            onClick={() => setPagination((p) => ({ ...p, page: p.page - 1 }))}
          >
            Anterior
          </button>
          <span>Página {pagination.page} de {pagination.pages}</span>
          <button
            type="button"
            disabled={pagination.page >= pagination.pages}
            onClick={() => setPagination((p) => ({ ...p, page: p.page + 1 }))}
          >
            Siguiente
          </button>
        </div>
      )}
    </div>
  );
};

export default BienestarBookingsManagement;
